import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import EDatePicker from '@/components/everridge/EDatePicker';
import EEnumPopupPicker from '@/components/everridge/EEnumPopupPicker';
import EListPopupPicker from '@/components/everridge/EListPopupPicker';
import { OrderStatus } from '@/features/orders/types/orders-dto';
import { OrdersPageFilterFormSchema } from '@/features/orders/validators/OrdersPageFilterValidator';
import { useOrdersFilter } from '@/features/orders/hooks/useOrdersFilter';
import OrderApartmentsLookup from '@/features/orders/components/lookups/OrderApartmentsLookup';
import OrderServicesLookup from '@/features/orders/components/lookups/OrderServicesLookup';
import OrderAuthorsLookup from './lookups/OrderAuthorsLookup';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { Roles } from '@/@types/auth.types.ts';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';

type FormData = z.infer<typeof OrdersPageFilterFormSchema>;

type Props = {
	onClose: () => void;
};

const OrdersPageFilter: FC<Props> = ({ onClose }) => {
	const [isOpen, setIsOpen] = useState(true);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();

	const { filter, setFilter } = useOrdersFilter();

	const userRole = useUserRole();

	const { control, handleSubmit, reset } = useForm<FormData>({
		resolver: zodResolver(OrdersPageFilterFormSchema),
		values: {
			startDate: filter.startDate,
			endDate: filter.endDate,
			orderStatus: filter.orderStatus,
			apartmentId: filter.apartmentId,
			serviceId: filter.serviceId,
			authorId: filter.authorId,
			amountRange: filter.amountRange,
		},
	});

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleSubmitFilterForm = (form: FormData) => {
		setFilter(form);
		handleClose();
	};

	const handleResetFilter = () => {
		reset({
			startDate: null,
			endDate: null,
			authorId: [],
			apartmentId: [],
			serviceId: [],
			amountRange: null,
			orderStatus: null,
		});
	};

	const orderStatuses = Object.values(OrderStatus).filter(
		(value) => typeof value === 'string',
	);

	const amountRanges = ['0-100', '100-200', '200-500', '500+'];

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t('elements.components.filter.title')}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
					<DialogDescription />
				</DialogHeader>
				<div className='flex flex-col gap-4 overflow-y-auto'>
					<div className='flex gap-4'>
						<EDatePicker
							control={control}
							fieldName={'startDate'}
							label={t('elements.form.startDate.label')}
							placeholder={t(
								'elements.form.startDate.placeholder',
							)}
						/>
						<EDatePicker
							control={control}
							fieldName={'endDate'}
							label={t('elements.form.endDate.label')}
							placeholder={t('elements.form.endDate.placeholder')}
						/>
					</div>
					{userRole === Roles.admin && (
						<OrderAuthorsLookup
							control={control}
							fieldName={'authorId'}
							selectVariant='multi'
							label={t('elements.form.authors.label')}
							placeholder={t('elements.form.authors.placeholder')}
						/>
					)}
					<OrderApartmentsLookup
						control={control}
						fieldName={'apartmentId'}
						selectVariant='multi'
						label={t('elements.form.apartments.label')}
						placeholder={t('elements.form.apartments.placeholder')}
					/>
					<OrderServicesLookup
						control={control}
						fieldName={'serviceId'}
						selectVariant='multi'
						label={t('elements.form.services.label')}
						placeholder={t('elements.form.services.placeholder')}
					/>
					<EListPopupPicker
						control={control}
						fieldName={'amountRange'}
						label={t('elements.form.amount.label')}
						placeholder={t('elements.form.amount.placeholder')}
						list={amountRanges}
					/>
					<EEnumPopupPicker
						control={control}
						fieldName={'orderStatus'}
						label={t('elements.form.statusPicker.label')}
						placeholder={t(
							'main.orders.components.form.statusOrder.placeholder',
						)}
						list={orderStatuses}
					/>
				</div>
				<DialogFooter
					className={cn(
						'justify-between',
						isMobile && 'justify-end mt-auto',
					)}>
					<Button
						variant='outline'
						onClick={handleResetFilter}>
						{t('elements.buttons.reset')}
					</Button>
					<Button onClick={handleSubmit(handleSubmitFilterForm)}>
						{t('elements.buttons.apply')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default OrdersPageFilter;
